import { Add, AddCircle, Delete, Group, Person } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import CustomTabPanel from '../core/CustomTabPanel';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import { DocumentOverview } from '../Document/DocumentOverview';
import { FSDirectorySearch } from '../FSDirectory/FSDirectorySearch';
import {
  useExportPDF,
  useGetDownloadedDocuments,
  useSendDocuments,
} from '../hooks/reactQuerys/DocumentQueries';
import { useGetHasDirectory } from '../hooks/reactQuerys/FsDirectoryQueries';
import { useGetLegalRequirementStates } from '../hooks/reactQuerys/LegalRequirementQueries';
import {
  useCreateLegalTransactionFullObject,
  useDeleteLegalTransaction,
  useGetLegalTransactionFullObject,
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
  useUpdateLegalTransactionFullObject,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetMeetingEmailTemplates } from '../hooks/reactQuerys/MeetingEmailTemplateQueries';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import { IDocument } from '../Interfaces/IDocument';
import { IDocumentDirectory } from '../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../Interfaces/IDocumentDirectoryHasDocument';
import { IDraftMessageFullObject } from '../Interfaces/IDraftMessage';
import { IInvoiceFullObject } from '../Interfaces/IInvoice';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../Interfaces/ILegalPhase';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { ILegalRole_canInvite } from '../Interfaces/ILegalRole_canInvite';
import {
  ILegalTransaction,
  ILegalTransactionDataCollection,
  ILegalTransactionFullObject,
} from '../Interfaces/ILegalTransaction';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { ILegalTransactionCustomId } from '../Interfaces/ILegalTransactionCustomId';
import { ILegalTransactionGwGFullObject } from '../Interfaces/ILegalTransactionGwG';
import { ILegalTransactionLog } from '../Interfaces/ILegalTransactionLog';
import { type IMeasure } from '../Interfaces/ILegalTransactionMeasure';
import { ILegalTransactionMeeting } from '../Interfaces/ILegalTransactionMeeting';
import { ILegalTransactionMeetingEmail } from '../Interfaces/ILegalTransactionMeetingEmail';
import { ILegaltransactionNote } from '../Interfaces/ILegaltransactionNote';
import { ILegalTransactionRecordNumber } from '../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionRelationship } from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionTransactionNumber } from '../Interfaces/ILegalTransactionTransactionNumber';
import type { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import {
  initSendDocumentNew,
  ISendDocumentNew,
  ISendDocumentNewSendOption,
  SendOption,
} from '../Interfaces/ISendDocumentNew';
import type { IUser } from '../Interfaces/IUser';
import type { IUserGroup } from '../Interfaces/IUserGroup';
import type { State } from '../redux/mainReducer';
import { a11yProps } from '../services/dialog.service';
import { AddCustomIdDialog } from './AddCustomIdDialog';
import { DocumentDirectoryOverview } from './DocumentDirectory/DocumentDirectoryOverview';
import { InvoiceTab } from './InvoiceTab/InvoiceTab';
import { LegalPhaseMain } from './LegalPhase/LegalPhaseMain';
import { LegalTransactionClientsOverview } from './LegalTransactionClientsOverview';
import { LegalTransactionDraftOverview } from './LegalTransactionDraftOverview';
import { SpecialFunctionOverview } from './LegalTransactionFunctions/SpecialFunctionOverview';
import { MeasureTab } from './LegalTransactionMeasure/MeasureTab';
import { LegalTransactionMeetingOverview } from './LegalTransactionMeeting/LegalTransactionMeetingOverview';
import { LegalTransactionDataCollection } from './LegalTransactoinRelationships/LegalTransactionDataCollection';
import { NoteTab } from './Notes/NoteTab';
import { SendDocumentNew } from './SendDocuments/SendDocumentNew';
import { SidepanelView } from './SidepanelView';
import { handleInvoiceReminder } from './utilities';

interface IProps {
  idLegalTransaction: number;
  handleClose: (isEdited: boolean) => void;
  users: IUser[];
  userGroups: IUserGroup[];
  legalTransactionTypes: ILegalTransactionType[];
  legalClients: ILegalClient[];
  arnoTopObjects?: IArnoTopObject[];
}

export const SidepanelDocumentContext = createContext<{
  selectedSidepanelDocument: IDocument | undefined;
  setSelectedSidepanelDocument: (document: IDocument) => void;
}>({
  selectedSidepanelDocument: undefined,
  setSelectedSidepanelDocument: () => {},
});

export const useSidepanelDocumentContext = () => {
  return useContext(SidepanelDocumentContext);
};

const convertToBoolean = (value: unknown) => {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (typeof value === 'number') {
    return Boolean(value);
  } else {
    return !!value;
  }
};

const getEmptyLegalTransaction = () => {
  return {
    idLegalTransaction: -1,
    Created_at: '2020-02-02',
    Closed_at: null,
    idContact: -1,
    idLegalTransactionState: 1,
    idLegalTransactionType: -1,
    idLegalTransactionProgress: 10,
    idUserGroup: null,
    UserGroup: '',
    Title: '',
    Notary_idUser: -1,
    Remarks: null,
    Employee_idUser: null,
    LastPhaseUpdate_at: null,
    NotarizationDate: null,
    LegalClientArray: [],
    StartEditDate: null,
    UserInformed_LackOfActivity: false,
    LastActivity_at: null,
    // ARNOtop Sync
    idArnoTopObject: null,
  } as ILegalTransaction;
};

export const LegalTransactionEdit = ({
  idLegalTransaction,
  handleClose,
  users,
  userGroups,
  legalTransactionTypes,
  legalClients,
}: IProps) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();

  const {
    hasDirectory: hasDirCertificate,
    isLoadingHasDirectory: isLoadingHasDirCertificate,
  } = useGetHasDirectory(1);
  const {
    hasDirectory: hasDirInvoice,
    isLoadingHasDirectory: isLoadingHasDirInvoice,
  } = useGetHasDirectory(2);

  const {
    legalTransactionStates,
    isLoadingLegalTransactionStates,
    refetchLegalTransactionStates,
  } = useGetLegalTransactionStates();

  const {
    legalTransactionProgresses,
    isLoadingLegalTransactionProgresses,
    refetchLegalTransactionProgresses,
  } = useGetLegalTransactionProgresses();

  const {
    legalRequirementStates,
    isLoadingLegalRequirementStates,
    refetchLegalRequirementStates,
  } = useGetLegalRequirementStates();

  const {
    meetingEmailTemplates,
    isLoadingMeetingEmailTemplates,
    refetchMeetingEmailTemplates,
  } = useGetMeetingEmailTemplates();

  const {
    createLegalTransactionFullObject,
    isPendingCreateLegalTransactionFullObject,
  } = useCreateLegalTransactionFullObject();

  const {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  } = useUpdateLegalTransactionFullObject();

  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(idLegalTransaction);

  const {
    downloadedDocuments,
    isLoadingDownloadedDocuments,
    refetchDownloadedDocuments,
  } = useGetDownloadedDocuments();

  const { deleteLegalTransaction, isPendingDeleteLegalTransaction } =
    useDeleteLegalTransaction();

  const { sendDocuments } = useSendDocuments();

  const { exportPDF, isPendingExportPDF } = useExportPDF();

  const mimeTypes = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const combinedOptions = [
    ...users
      .filter(user => !user.isNotary)
      .map(user => ({
        type: 'Mitarbeiter',
        id: user.idUser,
        name: `${user.FirstName} ${user.LastName}`,
      })),
    ...userGroups.map(group => ({
      type: 'Gruppe',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
  ];

  const [isClosed, setIsClosed] = useState(false);
  const [measures, setMeasures] = useState<IMeasure[]>([]);
  const [dataCollectionArray, setDataCollectionArray] = useState<
    ILegalTransactionDataCollection[]
  >([]);

  const [relationships, setRelationships] = useState<
    ILegalTransactionRelationship[]
  >([]);

  const [hasInitialized, setHasInitialized] = useState(false);

  const [recordNumberArray, setRecordNumberArray] = useState<
    ILegalTransactionRecordNumber[]
  >([]);
  const [transactionNumberArray, setTransactionNumberArray] = useState<
    ILegalTransactionTransactionNumber[]
  >([]);
  const [legalTransactionObject, setLegalTransactionObject] = useState(
    getEmptyLegalTransaction()
  );

  const [certificationArray, setCertificationArray] = useState<IDocument[]>([]);
  const [invoiceArray, setInvoiceArray] = useState<IDocument[]>([]);
  const [invoiceObjectArray, setInvoiceObjectArray] = useState<
    IInvoiceFullObject[]
  >([]);
  const [draftArray, setDraftArray] = useState<IDocument[]>([]);
  const [draftMessageArray, setDraftMessageArray] = useState<
    IDraftMessageFullObject[]
  >([]);
  const [noteArray, setNoteArray] = useState<ILegaltransactionNote[]>([]);
  const [documentArray, setDocumentArray] = useState<
    IDocumentDirectoryHasDocument[]
  >([]);
  const [documentDirectoryArray, setDocumentDirectoryArray] = useState<
    IDocumentDirectory[]
  >([]);
  const [meetingArray, setMeetingArray] = useState<ILegalTransactionMeeting[]>(
    []
  );
  const [meetingEmailArray, setMeetingEmailArray] = useState<
    ILegalTransactionMeetingEmail[]
  >([]);

  const [logArray, setLogArray] = useState<ILegalTransactionLog[]>([]);

  const [legalPhaseFullObjectArray, setLegalPhaseFullObjectArray] = useState<
    ILegalPhaseFullObject[]
  >([]);
  const [legalRoles, setLegalRoles] = useState<ILegalRole[]>([]);
  const [legalRoleCanInviteMap, setLegalRoleCanInviteMap] = useState<
    ILegalRole_canInvite[]
  >([]);
  const [gwgArray, setGwgArray] = useState<ILegalTransactionGwGFullObject[]>(
    []
  );
  const [currentTab, setCurrentTab] = useState(5);

  const [
    showSendDocumentsDialogConfirmation,
    setShowSendDocumentsDialogConfirmation,
  ] = useState(false);

  const [takenLegalClients, setTakenLegalClients] = useState<ILegalClient[]>(
    []
  );
  const [customIds, setCustomIds] = useState<ILegalTransactionCustomId[]>([]);
  const [isOpenChangeType, setIsOpenChangeType] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [
    legalTransaction_has_LegalTransactionSpecialFunctionArray,
    setLegalTransaction_has_LegalTransactionSpecialFunctionArray,
  ] = useState<ILegalTransaction_has_LegalTransactionSpecialFunction[]>([]);

  const [selectedType, setSelectedType] = useState('user');

  const theme = useTheme();

  const [showAddCustomIdDialog, setShowAddCustomIdDialog] = useState(false);

  const [savedLegalTransaction, setSavedLegalTransaction] = useState<
    ILegalTransactionFullObject | undefined
  >(undefined);
  const [isSavePossible, setIsSavePossible] = useState<
    (boolean | Record<string, boolean>)[]
  >([]);

  const isInitialized = isSavePossible.length > 0;

  ////////////////////////// NEU BAUMERT UNTERLAGEN SENDEN

  const [showSendDocumentsDialogNew, setShowSendDocumentsDialogNew] =
    useState(false);

  const [sendDocumentNew, setSendDocumentNew] = useState<ISendDocumentNew>(() =>
    initSendDocumentNew(idLegalTransaction)
  );

  useEffect(() => {
    if (dataCollectionArray.length > 0 && !isInitialized) {
      const newSaveState = dataCollectionArray.map(item => {
        if ('idMortgage' in item) {
          return {
            accordionOwners: true,
            accordionRealEstate: true,
            accordionMortgageDetails: true,
            accordionBeneficiary: true,
          };
        } else {
          return true;
        }
      });

      setIsSavePossible(newSaveState);
    }
  }, [dataCollectionArray]);

  useEffect(() => {
    isButtonDisabled();
  }, [isSavePossible]);

  const isButtonDisabled = () => {
    // Iteriere durch das Array
    for (const item of isSavePossible) {
      if (typeof item === 'boolean') {
        // Wenn es ein boolean ist, überprüfen, ob es true ist
        if (!item) {
          return true; // Ein false bedeutet, dass der Button deaktiviert werden soll
        }
      } else if (typeof item === 'object' && item !== null) {
        // Wenn es ein Record ist, überprüfe, ob alle Werte true sind
        const allTrue = Object.values(item).every(value => value === true);
        if (!allTrue) {
          return true; // Ein false in einem der Werte bedeutet, dass der Button deaktiviert werden soll
        }
      }
    }

    // Wenn wir hier sind, bedeutet das, dass alle Werte true sind
    return false; // Wenn alles `true` ist, soll der Button aktiv bleiben
  };

  const localSetter = (legalTransactionToSet: ILegalTransactionFullObject) => {
    legalTransactionToSet.UserInformed_LackOfActivity = convertToBoolean(
      legalTransactionToSet.UserInformed_LackOfActivity
    );

    setIsClosed(
      legalTransactionToSet.Closed_at !== null ||
        legalTransactionToSet.idLegalTransactionState === 100
    );
    setDataCollectionArray(legalTransactionToSet.DataCollection);
    setRelationships(legalTransactionToSet.RelationshipArray ?? []);

    setLegalTransactionObject(legalTransactionToSet);
    setSelectedType(() => {
      return legalTransactionToSet.Employee_idUser !== null ||
        legalTransactionToSet.idUserGroup === null
        ? 'user'
        : 'group';
    });

    setGwgArray(legalTransactionToSet.LegalTransactionGwGArray);
    setTakenLegalClients(legalTransactionToSet.LegalClientArray);
    setLegalRoles(legalTransactionToSet.LegalRoleArray);
    setLegalRoleCanInviteMap(legalTransactionToSet.LegalRoleCanInviteArray);

    setCertificationArray(legalTransactionToSet.CertificateArray);
    setInvoiceArray(legalTransactionToSet.InvoiceArray);
    setInvoiceObjectArray(legalTransactionToSet.InvoiceObjectArray);
    setDraftArray(legalTransactionToSet.DraftArray);
    setDraftMessageArray(legalTransactionToSet.DraftMessageArray);

    setNoteArray(legalTransactionToSet.NoteArray);

    // Setzen der Ablage
    setDocumentDirectoryArray(legalTransactionToSet.DocumentDirectoryArray);
    setDocumentArray(legalTransactionToSet.DocumentArray);
    // Termine
    setMeetingArray(legalTransactionToSet.LegalTransactionMeetingArray);
    setMeetingEmailArray(
      legalTransactionToSet.LegalTransactionMeetingEmailArray
    );

    setMeasures(legalTransactionToSet.Measurements || []);

    if (legalTransactionToSet.customIds) {
      setCustomIds(legalTransactionToSet.customIds);
    }

    //Recordnumber und Transactionnumber
    //prüfe ob Recordnumber und Transactionnumber vorhanden sind, wenn nicht, dann füge ein leeres Element hinzu
    if (legalTransactionToSet.RecordNumberArray.length === 0) {
      setRecordNumberArray([
        {
          RecordNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
    } else {
      setRecordNumberArray(legalTransactionToSet.RecordNumberArray);
    }
    if (legalTransactionToSet.TransactionNumberArray.length === 0) {
      setTransactionNumberArray([
        {
          TransactionNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
    } else {
      setTransactionNumberArray(legalTransactionToSet.TransactionNumberArray);
    }
    setLegalTransaction_has_LegalTransactionSpecialFunctionArray(
      legalTransactionToSet.LegalTransaction_has_LegalTransactionSpecialFunctionArray
    );

    // Prüfen nach Log
    if (legalTransactionToSet.LogArray !== undefined) {
      setLogArray(legalTransactionToSet.LogArray);
    }

    setLegalPhaseFullObjectArray([
      ...legalTransactionToSet.LegalPhaseArray.map((currentLegalPhase, idx) =>
        currentLegalPhase.Position !== null
          ? {
              ...currentLegalPhase,
              isComplete: Boolean(currentLegalPhase.isComplete),
            }
          : {
              ...currentLegalPhase,
              Position: idx + 1,
              isComplete: Boolean(currentLegalPhase.isComplete),
            }
      ),
    ]);

    setHasInitialized(true);
  };

  useEffect(() => {
    if (!hasInitialized || savedLegalTransaction) return;
    const currentLegalTransactionObject = createLegalTransactionSaveObject();
    setSavedLegalTransaction(currentLegalTransactionObject);
  }, [hasInitialized, savedLegalTransaction]);

  useEffect(() => {
    if (!legalTransactionFullObject) return;
    localSetter(legalTransactionFullObject);
  }, [legalTransactionFullObject]);

  const checkIfEdited = () =>
    !isEqual(createLegalTransactionSaveObject(), savedLegalTransaction);

  const createLegalTransactionSaveObject = () => {
    const uploadObject: ILegalTransactionFullObject = {
      ...legalTransactionObject,
      UserInformed_LackOfActivity: Boolean(
        legalTransactionObject.UserInformed_LackOfActivity
      ),
      DataCollection: dataCollectionArray,
      RelationshipArray: relationships,
      LegalPhaseArray: legalPhaseFullObjectArray,
      LegalClientArray: takenLegalClients,
      LegalTransactionGwGArray: gwgArray,
      CertificateArray: certificationArray,
      InvoiceArray: invoiceArray,
      InvoiceObjectArray: invoiceObjectArray,
      DraftArray: draftArray,
      DraftMessageArray: draftMessageArray,
      NoteArray: noteArray,
      LegalRoleArray: legalRoles,
      LegalRoleCanInviteArray: legalRoleCanInviteMap,
      LegalTransactionMeetingArray: meetingArray,
      LegalTransactionMeetingEmailArray: meetingEmailArray,
      DocumentArray: documentArray,
      DocumentDirectoryArray: documentDirectoryArray,
      LegalTransaction_has_LegalTransactionSpecialFunctionArray:
        legalTransaction_has_LegalTransactionSpecialFunctionArray,
      //remove empty recordNumbers
      RecordNumberArray: recordNumberArray.filter(
        recordNumber => recordNumber.RecordNumberEntry !== ''
      ),
      TransactionNumberArray: transactionNumberArray.filter(
        transactionNumber => transactionNumber.TransactionNumberEntry !== ''
      ),
      customIds,
      Measurements: measures,
    };
    return uploadObject;
  };

  const transactionInputRef = useRef<HTMLInputElement>(null);
  const recordInputRef = useRef<HTMLInputElement>(null);
  const [focusTransaction, setFocusTransaction] = useState(false);
  const [focusRecord, setFocusRecord] = useState(false);
  const [selectedSidepanelDocument, setSelectedSidepanelDocument] = useState<
    IDocument | undefined
  >(undefined);

  const isPending =
    isPendingCreateLegalTransactionFullObject ||
    isPendingUpdateLegalTransactionFullObject ||
    isPendingDeleteLegalTransaction;

  const customIdsWithContact = useMemo(
    () =>
      customIds.map(customId => ({
        ...customId,
        legalClient: legalClients.find(
          c => c.idLegalClient === customId.idLegalClient
        )!,
      })),
    [customIds, legalClients]
  );

  const hasMoreCustomIds = useMemo(
    () => customIdsWithContact.length < takenLegalClients.length,
    [customIdsWithContact.length, takenLegalClients.length]
  );

  useEffect(() => {
    if (focusTransaction && transactionInputRef.current) {
      transactionInputRef.current.focus();
      setFocusTransaction(false);
    }
  }, [focusTransaction]);

  useEffect(() => {
    if (focusRecord && recordInputRef.current) {
      recordInputRef.current.focus();
      setFocusRecord(false);
    }
  }, [focusRecord]);

  const handleSave = async (migrateType?: boolean) => {
    setIsOpenChangeType(false);

    const uploadObject = createLegalTransactionSaveObject();
    uploadObject.migrateType = migrateType;

    const isConfirm = await handleInvoiceReminder(uploadObject, theme);

    if (!isConfirm) {
      setCurrentTab(5);
      return;
    }

    try {
      if (legalTransactionObject.idLegalTransaction < 0) {
        const newData = await createLegalTransactionFullObject(uploadObject);
        localSetter(newData);
      } else {
        const newData = await updateLegalTransactionFullObject(uploadObject);
        localSetter(newData);
      }

      setSavedLegalTransaction(undefined);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich gespeichert!',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Speichern des Vorgangs!',
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLegalTransaction(idLegalTransaction);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      navigate(`/legaltransaction`);

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich gelöscht!',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Löschen des Vorgangs!',
      });
    }
  };

  const handleDeleteRecordNumber = (index: number) => {
    if (recordNumberArray.length > 1) {
      const newArray = recordNumberArray.filter((_, i) => i !== index);
      setRecordNumberArray(newArray);
    }
  };

  const handleAddRecordNumber = () => {
    if (
      recordNumberArray[recordNumberArray.length - 1].RecordNumberEntry !== ''
    ) {
      setRecordNumberArray([
        ...recordNumberArray,
        { RecordNumberEntry: '', idLegalTransaction: idLegalTransaction },
      ]);
      setFocusRecord(true);
    }
  };

  const handleChangeRecordNumber = (index: number, value: string) => {
    setRecordNumberArray(
      recordNumberArray.map((recordNumber, i) =>
        i === index
          ? { ...recordNumber, RecordNumberEntry: value }
          : recordNumber
      )
    );
  };

  const handleSendDocuments = async () => {
    if (
      sendDocumentNew.legalClientArray.length > 0 &&
      sendDocumentNew.documentTypeArray.length > 0 &&
      sendDocumentNew.emailText !== ''
    ) {
      try {
        await sendDocuments(sendDocumentNew);

        showAlert({
          severity: 'success',
          text: 'Dokumente erfolgreich versendet',
        });
      } catch (error) {
        showAlert({
          severity: 'error',
          text: 'Fehler beim Versenden der Dokumente',
        });
      }
    }
  };

  const handleDeleteTransactionNumber = (index: number) => {
    if (transactionNumberArray.length > 1) {
      const newArray = transactionNumberArray.filter((_, i) => i !== index);
      setTransactionNumberArray(newArray);
    }
  };

  const handleChangeTransactionNumber = (index: number, value: string) => {
    setTransactionNumberArray(
      transactionNumberArray.map((transactionNumber, i) =>
        i === index
          ? { ...transactionNumber, TransactionNumberEntry: value }
          : transactionNumber
      )
    );
  };

  const handleChangeCustomIds = (contactId: number, customId: string) => {
    setCustomIds(prevCustomIds => {
      if (prevCustomIds.find(item => item.idLegalClient === contactId)) {
        return prevCustomIds.map(item =>
          item.idLegalClient === contactId
            ? { ...item, customId: customId }
            : item
        );
      } else {
        return [
          ...prevCustomIds,
          {
            idLegalTransaction: idLegalTransaction,
            idLegalClient: contactId,
            customId,
          },
        ];
      }
    });
  };

  const handleExportPdf = async () => {
    if (!legalTransactionFullObject) {
      return;
    }
    try {
      const uploadObject = {
        ...legalTransactionFullObject,
        UserInformed_LackOfActivity: Boolean(
          legalTransactionFullObject.UserInformed_LackOfActivity
        ),
      };
      const response = await exportPDF(uploadObject);
      const linkSource = `data:application/pdf;base64,${response.pdf}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = response.fileName;
      downloadLink.click();
    } catch (error) {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Exportieren des PDF',
      });
    }
  };

  const handleDeleteCustomId = (contactId: number) => {
    setCustomIds(prevCustomIds =>
      prevCustomIds.filter(
        customId =>
          customId.idLegalClient !== contactId &&
          customId.idLegalTransaction === idLegalTransaction
      )
    );
  };

  const handleAddTransactionNumber = () => {
    if (
      transactionNumberArray[transactionNumberArray.length - 1]
        .TransactionNumberEntry !== ''
    ) {
      setTransactionNumberArray([
        ...transactionNumberArray,
        {
          TransactionNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
      setFocusTransaction(true);
    }
  };

  if (
    isLoadingDownloadedDocuments ||
    isLoadingHasDirCertificate ||
    isLoadingHasDirInvoice ||
    isLoadingLegalTransactionStates ||
    isLoadingLegalTransactionProgresses ||
    isLoadingLegalRequirementStates ||
    isLoadingMeetingEmailTemplates ||
    isFetchingLegalTransactionFullObject
  )
    return <LoadingDialog unmount={() => handleClose(false)} />;

  if (
    legalTransactionFullObject === undefined ||
    downloadedDocuments === undefined ||
    legalTransactionStates === undefined ||
    legalTransactionProgresses === undefined ||
    legalRequirementStates === undefined ||
    meetingEmailTemplates === undefined
  )
    return (
      <NoDataDialog
        unmount={() => handleClose(false)}
        refetch={() => {
          refetchLegalTransactionFullObject();
          refetchDownloadedDocuments();
          refetchLegalTransactionStates();
          refetchLegalTransactionProgresses();
          refetchLegalRequirementStates();
          refetchMeetingEmailTemplates();
        }}
      />
    );

  return (
    <SidepanelDocumentContext.Provider
      value={{
        selectedSidepanelDocument,
        setSelectedSidepanelDocument,
      }}
    >
      <Dialog
        open
        onClose={() => {
          if (isPendingUpdateLegalTransactionFullObject) return;
          handleClose(checkIfEdited());
        }}
        maxWidth='xl'
        fullWidth
        scroll='paper'
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            p: 2,
          }}
        >
          <Typography variant='h5'>
            {idLegalTransaction > 0 && legalTransactionFullObject
              ? legalTransactionFullObject.Title
              : 'Neues Rechtsgeschäft'}
          </Typography>

          {/* Container für die Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='outlined'
              onClick={() => {
                setShowSendDocumentsDialogNew(true);
              }}
            >
              Unterlagen senden
            </Button>
            <Button
              variant='outlined'
              onClick={() => {
                handleExportPdf();
              }}
            >
              {isPendingExportPDF ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Export Nebenakte</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Export Nebenakte'
              )}
            </Button>

            {idLegalTransaction > 0 && legalTransactionFullObject && (
              <IconButton
                disabled={isClosed}
                onClick={() => setIsOpenDelete(true)}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        </Box>

        <DialogContent>
          {isPending && <LinearProgress sx={{ mt: 1 }} />}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {isClosed && (
              <Alert severity='warning'>
                Dieser Vorgang ist bereits geschlossen und kann nicht mehr
                angepasst werden.
              </Alert>
            )}

            {legalTransactionObject &&
              legalTransactionObject.idLegalTransactionState === 1 && (
                <Alert severity='warning'>
                  Im Status "Entwurf" kann der Mandant im Mandantenportal nicht
                  auf diesen Vorgang zugreifen und erhält keine E-Mails dazu.
                </Alert>
              )}

            {legalTransactionObject &&
              legalTransactionFullObject &&
              legalTransactionObject.idLegalTransactionType !==
                legalTransactionFullObject.idLegalTransactionType && (
                <Alert severity='warning'>
                  Sie haben die Rechtsgechäftsart verändert. Dieser Vorgang muss
                  zur neuen Rechtsgechäftsart migriert werden!
                </Alert>
              )}
          </Box>

          <Box
            sx={{
              display: selectedSidepanelDocument ? 'flex' : 'block',
              gap: 4,
              overflowX: 'hidden',
            }}
          >
            {selectedSidepanelDocument && (
              <Box
                sx={{
                  flexBasis: '35%',
                  maxWidth: '35%',
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                <SidepanelView
                  document={selectedSidepanelDocument}
                  mimeTypes={mimeTypes}
                  onClose={() => setSelectedSidepanelDocument(undefined)}
                />
              </Box>
            )}
            <Box
              sx={{
                flexShrink: 1,
                flexGrow: 0,
                maxWidth: selectedSidepanelDocument ? '61%' : 'auto',
                flexBasis: selectedSidepanelDocument ? '61%' : 'auto',
              }}
            >
              <Grid container spacing={2} mt={1}>
                <Grid item sm={12}>
                  <TextField
                    label='Bezeichnung'
                    value={legalTransactionObject.Title}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        Title: event.target.value,
                      })
                    }
                    size='small'
                    fullWidth
                    disabled={isClosed}
                  />
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label='Art'
                    value={legalTransactionObject.idLegalTransactionType}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        idLegalTransactionType: Number(event.target.value),
                      })
                    }
                    size='small'
                    fullWidth
                    select
                  >
                    {legalTransactionTypes.map(legalTransactionType => (
                      <MenuItem
                        key={`idLegalTransactionType-${legalTransactionType.idLegalTransactionType}`}
                        value={legalTransactionType.idLegalTransactionType}
                      >
                        {legalTransactionType.LegalTransactionType}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label='Zugewiesener Notar'
                    value={legalTransactionObject.Notary_idUser}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        Notary_idUser: Number(event.target.value),
                      })
                    }
                    size='small'
                    fullWidth
                    select
                    disabled={isClosed}
                  >
                    {users
                      .filter(user => Boolean(user.isNotary))
                      .map(user => (
                        <MenuItem
                          key={`Notary_idUser-${user.idUser}`}
                          value={user.idUser}
                        >
                          {user.FirstName} {user.LastName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                <Grid item sm={3}>
                  <Autocomplete
                    options={combinedOptions}
                    groupBy={option => option.type}
                    getOptionLabel={option => option.name}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          selectedType === 'user' ? (
                            <span>
                              <Person
                                style={{
                                  verticalAlign: 'middle',
                                  marginRight: 8,
                                }}
                              />
                              Zugewiesener Mitarbeiter
                            </span>
                          ) : (
                            <span>
                              <Group
                                style={{
                                  verticalAlign: 'middle',
                                  marginRight: 8,
                                }}
                              />
                              Zugewiesene Gruppe
                            </span>
                          )
                        }
                        size='small'
                        variant='outlined'
                      />
                    )}
                    renderOption={(props, option) => (
                      <ListItem {...props} key={option.id}>
                        <ListItemText primary={option.name} />
                      </ListItem>
                    )}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        if (newValue.type === 'Mitarbeiter') {
                          setLegalTransactionObject({
                            ...legalTransactionObject,
                            Employee_idUser: newValue.id,
                            idUserGroup: null,
                          });
                          setSelectedType('user');
                        } else if (newValue.type === 'Gruppe') {
                          setLegalTransactionObject({
                            ...legalTransactionObject,
                            idUserGroup: newValue.id,
                            Employee_idUser: null,
                          });
                          setSelectedType('group');
                        }
                      } else {
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          Employee_idUser: null,
                          idUserGroup: null,
                        });
                      }
                    }}
                    value={
                      selectedType === 'user' &&
                      legalTransactionObject.Employee_idUser !== null
                        ? combinedOptions.find(
                            option =>
                              option.type === 'Mitarbeiter' &&
                              option.id ===
                                legalTransactionObject.Employee_idUser
                          )
                        : selectedType === 'group' &&
                            legalTransactionObject.idUserGroup !== null
                          ? combinedOptions.find(
                              option =>
                                option.type === 'Gruppe' &&
                                option.id === legalTransactionObject.idUserGroup
                            )
                          : null
                    }
                  />
                </Grid>
                <Grid item sm={3}>
                  <TextField
                    label='Datum der Beurkundung'
                    placeholder=''
                    type='date'
                    value={legalTransactionObject.NotarizationDate}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        NotarizationDate: event.target.value,
                      })
                    }
                    size='small'
                    disabled={isClosed}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label='Phase'
                    value={legalTransactionObject.idLegalTransactionProgress}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        idLegalTransactionProgress: Number(event.target.value),
                      })
                    }
                    size='small'
                    fullWidth
                    select
                    disabled={isClosed}
                  >
                    {legalTransactionProgresses.map(
                      legalTransactionProgress => (
                        <MenuItem
                          key={`idLegalTransactionProgress-${legalTransactionProgress.idLegalTransactionProgress}`}
                          value={
                            legalTransactionProgress.idLegalTransactionProgress
                          }
                        >
                          {legalTransactionProgress.LegalTransactionProgress}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Grid>

                <Grid item sm={3}>
                  <TextField
                    label='Status'
                    value={legalTransactionObject.idLegalTransactionState}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLegalTransactionObject({
                        ...legalTransactionObject,
                        idLegalTransactionState: Number(event.target.value),
                      })
                    }
                    size='small'
                    fullWidth
                    select
                    disabled={isClosed}
                  >
                    {legalTransactionStates.map(legalTransactionState => (
                      <MenuItem
                        key={`idLegalTransactionState-${legalTransactionState.idLegalTransactionState}`}
                        value={legalTransactionState.idLegalTransactionState}
                        disabled={legalTransactionState.isDisabled}
                      >
                        {legalTransactionState.LegalTransactionState}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item sm={3}>
                  {transactionNumberArray.map((transactionNumber, index) => (
                    <Box
                      key={`TransactionNumber-${index}`}
                      display='flex'
                      alignItems='center'
                    >
                      <TextField
                        label={`Vorgangsnummer`}
                        id={`TransactionNumber-${index}`}
                        value={transactionNumber.TransactionNumberEntry}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          handleChangeTransactionNumber(
                            index,
                            event.target.value
                          )
                        }
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                          if (event.target.value === '') {
                            handleDeleteTransactionNumber(index);
                          }
                        }}
                        size='small'
                        fullWidth
                        disabled={isClosed}
                        inputRef={
                          index === transactionNumberArray.length - 1
                            ? transactionInputRef
                            : null
                        }
                      />
                      {transactionNumberArray.length > 1 &&
                        index < transactionNumberArray.length - 1 &&
                        transactionNumberArray[index + 1]
                          ?.TransactionNumberEntry !== '' && (
                          <IconButton
                            onClick={() => handleDeleteTransactionNumber(index)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      {index === transactionNumberArray.length - 1 &&
                        transactionNumber.TransactionNumberEntry !== '' && (
                          <IconButton onClick={handleAddTransactionNumber}>
                            <AddCircle />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                </Grid>

                <Grid item sm={3}>
                  {recordNumberArray.map((recordNumber, index) => (
                    <Box
                      key={`RecordNumber-${index}`}
                      display='flex'
                      alignItems='center'
                    >
                      <TextField
                        label={`Urkundennummer`}
                        id={`RecordNumber-${index}`}
                        value={recordNumber.RecordNumberEntry}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          handleChangeRecordNumber(index, event.target.value)
                        }
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                          if (event.target.value === '') {
                            handleDeleteRecordNumber(index);
                          }
                        }}
                        size='small'
                        fullWidth
                        disabled={isClosed}
                        inputRef={
                          index === recordNumberArray.length - 1
                            ? recordInputRef
                            : null
                        }
                      />
                      {recordNumberArray.length > 1 &&
                        index < recordNumberArray.length - 1 &&
                        recordNumberArray[index + 1].RecordNumberEntry !==
                          '' && (
                          <IconButton
                            onClick={() => handleDeleteRecordNumber(index)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      {index === recordNumberArray.length - 1 &&
                        recordNumber.RecordNumberEntry !== '' && (
                          <IconButton onClick={handleAddRecordNumber}>
                            <AddCircle />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                </Grid>
                <Grid item sm={12}>
                  <Box
                    gap={1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='h6'>Mandantenkennungen</Typography>
                    <Tooltip
                      title={
                        !hasMoreCustomIds
                          ? 'Es wurde bereits jedem Mandanten eine Kennung zugewiesen.'
                          : undefined
                      }
                    >
                      <Box>
                        <Button
                          variant='text'
                          onClick={() => setShowAddCustomIdDialog(true)}
                          startIcon={<Add />}
                          disabled={!hasMoreCustomIds}
                        >
                          Hinzufügen
                        </Button>
                      </Box>
                    </Tooltip>
                  </Box>
                  <Grid item container sm={12} spacing={2} mt={1}>
                    {customIdsWithContact.map((x, i) => (
                      <Grid item sm={4} key={i}>
                        <TextField
                          key={i + 1}
                          label={`Mandantenkennung: ${x.legalClient?.FirstName} ${x.legalClient?.LastName}`}
                          value={x.customId}
                          size='small'
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            handleChangeCustomIds(
                              x.idLegalClient,
                              event.target.value
                            )
                          }
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <Tooltip title='Kennung löschen'>
                                <IconButton
                                  onClick={() =>
                                    handleDeleteCustomId(x.idLegalClient)
                                  }
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        >
                          {x.customId}
                        </TextField>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>

              {/*
              <DataCollectionArnoTopSyncForm
                dataCollectionArray={dataCollectionArray}
                relationshipDatacollectionScope={relationships}
                setRelationshipDatacollectionScope={setRelationships}
                setDataCollection={setDataCollectionArray}
                legalTransactionObject={legalTransactionObject}
                setLegalTransactionObject={setLegalTransactionObject}
                arnoTopObjects={arnoTopObjects}
              />
               */}

              <Box sx={{ my: 5 }}>
                <Stepper
                  activeStep={
                    legalTransactionObject?.idLegalTransactionProgress
                  }
                  alternativeLabel
                >
                  {legalTransactionProgresses.map(legalTransactionProgress => (
                    <Step
                      key={legalTransactionProgress.idLegalTransactionProgress}
                      completed={
                        Number(
                          legalTransactionObject?.idLegalTransactionProgress
                        ) > legalTransactionProgress.idLegalTransactionProgress
                      }
                      active={
                        Number(
                          legalTransactionObject?.idLegalTransactionProgress
                        ) ===
                        legalTransactionProgress.idLegalTransactionProgress
                      }
                    >
                      <StepLabel>
                        {legalTransactionProgress.LegalTransactionProgress}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Grid container>
                <Grid item md={2} xs={3}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs
                      value={currentTab}
                      onChange={(_, newValue) => setCurrentTab(newValue)}
                      variant='scrollable'
                      orientation='vertical'
                    >
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Vorgangsmanager'
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Datenerfassung'
                        {...a11yProps(1)}
                      />

                      <Tab
                        sx={{ alignItems: 'start' }}
                        label={
                          <Badge
                            badgeContent={
                              noteArray.filter(
                                x => x.idLegalTransactionNote > 0
                              ).length
                            }
                            color='primary'
                            sx={{
                              '& .MuiBadge-badge': {
                                right: -10,
                              },
                            }}
                          >
                            Notizen
                          </Badge>
                        }
                        {...a11yProps(2)}
                      />

                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Entwurf'
                        {...a11yProps(3)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Termine'
                        {...a11yProps(4)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Abwicklung'
                        {...a11yProps(5)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Mandanten'
                        {...a11yProps(6)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Urkunden'
                        {...a11yProps(7)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Rechnungen'
                        {...a11yProps(8)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Interne Datenablage'
                        {...a11yProps(9)}
                      />
                      <Tab
                        sx={{ alignItems: 'start' }}
                        label='Funktionen'
                        {...a11yProps(10)}
                      />
                      {logArray.length > 0 && (
                        <Tab
                          sx={{ alignItems: 'start' }}
                          label='Log'
                          {...a11yProps(11)}
                        />
                      )}
                    </Tabs>
                  </Box>
                </Grid>

                <Grid item md={10} xs={9}>
                  <CustomTabPanel value={currentTab} index={0}>
                    <MeasureTab
                      measures={measures}
                      setMeasures={setMeasures}
                      idLegalTransaction={idLegalTransaction}
                      legalTransactionHasSpecialFunctions={
                        legalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      setLegalTransactionHasSpecialFunctions={
                        setLegalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      gwgs={gwgArray}
                      setGwgs={setGwgArray}
                      legalPhaseFullObjects={legalPhaseFullObjectArray}
                      legalRequirementStates={legalRequirementStates}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={1}>
                    <LegalTransactionDataCollection
                      isSavePossible={isSavePossible}
                      relationships={relationships}
                      setRelationships={setRelationships}
                      setIsSavePossible={setIsSavePossible}
                      dataCollection={dataCollectionArray}
                      setDataCollection={setDataCollectionArray}
                      setTakenLegalClients={setTakenLegalClients}
                      takenLegalClients={takenLegalClients}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={2}>
                    <NoteTab
                      notesArray={noteArray}
                      setNotesArray={setNoteArray}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={3}>
                    <LegalTransactionDraftOverview
                      draftArray={draftArray}
                      setDraftArray={setDraftArray}
                      draftMessageArray={draftMessageArray}
                      setDraftMessageArray={setDraftMessageArray}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={4}>
                    <LegalTransactionMeetingOverview
                      idLegalTransactionType={
                        legalTransactionObject.idLegalTransactionType
                      }
                      meetingEmailTemplateArray={meetingEmailTemplates}
                      meetingArray={meetingArray}
                      setMeetingArray={setMeetingArray}
                      meetingEmailArray={meetingEmailArray}
                      setMeetingEmailArray={setMeetingEmailArray}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={5}>
                    <LegalPhaseMain
                      legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                      setLegalPhaseFullObjectArray={
                        setLegalPhaseFullObjectArray
                      }
                      legalReqState={legalRequirementStates}
                      downloadedArray={downloadedDocuments}
                      legaltransactionId={idLegalTransaction}
                      startdatedeadline={
                        legalTransactionObject.NotarizationDate &&
                        legalTransactionObject.NotarizationDate
                          ? new Date(legalTransactionObject.NotarizationDate)
                          : legalTransactionObject.StartEditDate &&
                              legalTransactionObject.StartEditDate
                            ? new Date(legalTransactionObject.StartEditDate)
                            : undefined
                      }
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={currentTab} index={6}>
                    <LegalTransactionClientsOverview
                      setCustomIds={setCustomIds}
                      legalRoles={legalRoles}
                      setLegalRoles={setLegalRoles}
                      legalRoleCanInviteMap={legalRoleCanInviteMap}
                      setLegalRoleCanInviteMap={setLegalRoleCanInviteMap}
                      takenLegalClients={takenLegalClients}
                      setTakenLegalClients={setTakenLegalClients}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={7}>
                    <DocumentOverview
                      key='certification'
                      allowRemove={true}
                      allowUpload={true}
                      title='Urkunden'
                      documentArray={certificationArray}
                      setDocumentArray={setCertificationArray}
                      downloadedArray={downloadedDocuments}
                      enableSidepanel={true}
                    />
                    {hasDirCertificate && legalTransactionFullObject && (
                      <Accordion sx={{ mt: 5 }}>
                        <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                          Ordnersuche
                        </AccordionSummary>
                        <AccordionDetails>
                          <FSDirectorySearch
                            key='certification-fs-search'
                            searchArray={recordNumberArray
                              .filter(
                                recordNumber =>
                                  recordNumber.RecordNumberEntry !== ''
                              )
                              .map(
                                recordNumber => recordNumber.RecordNumberEntry
                              )}
                            searchItem='certificate'
                            documentArray={certificationArray}
                            setDocumentArray={setCertificationArray}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={8}>
                    <>
                      <InvoiceTab
                        legalTransaction={legalTransactionObject}
                        documentArray={invoiceArray}
                        setDocumentArray={setInvoiceArray}
                        downloadedArray={downloadedDocuments}
                        invoiceObjectArray={invoiceObjectArray}
                        setInvoiceObjectArray={setInvoiceObjectArray}
                        legalClients={takenLegalClients}
                        setLegalClients={setTakenLegalClients}
                        legalRoles={legalRoles}
                        legalTransactionTypes={legalTransactionTypes}
                      />
                      {hasDirInvoice && legalTransactionFullObject && (
                        <Accordion sx={{ mt: 5 }}>
                          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                            Ordnersuche
                          </AccordionSummary>
                          <AccordionDetails>
                            <FSDirectorySearch
                              key='invoice-fs-search'
                              searchArray={recordNumberArray
                                .filter(
                                  recordNumber =>
                                    recordNumber.RecordNumberEntry !== ''
                                )
                                .map(
                                  recordNumber => recordNumber.RecordNumberEntry
                                )}
                              searchItem='invoice'
                              documentArray={invoiceArray}
                              setDocumentArray={setInvoiceArray}
                            />
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </>
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={9}>
                    <DocumentDirectoryOverview
                      idLegalTransaction={idLegalTransaction}
                      documentArray={documentArray}
                      documentDirectoryArray={documentDirectoryArray}
                      setDocumentArray={setDocumentArray}
                      setDocumentDirectoryArray={setDocumentDirectoryArray}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={10}>
                    <SpecialFunctionOverview
                      idLegalTransaction={idLegalTransaction}
                      legalTransaction_has_LegalTransactionSpecialFunctionArray={
                        legalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
                        setLegalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      gwgArray={gwgArray}
                      setGwgArray={setGwgArray}
                      legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                      legalReqState={legalRequirementStates}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={currentTab} index={11}>
                    <Typography variant='h6'>Log</Typography>
                    <Grid sx={{ mt: 2 }} container spacing={1}>
                      {logArray.map(log => (
                        <React.Fragment key={log.Timestamp}>
                          <Grid item xs={4} sm={2}>
                            {new Date(log.Timestamp).toLocaleString('de-DE')}
                          </Grid>
                          <Grid item xs={8} sm={10}>
                            {log.Log}
                          </Grid>
                        </React.Fragment>
                      ))}
                    </Grid>
                  </CustomTabPanel>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {isButtonDisabled() && (
            <Alert severity='warning' sx={{ marginBottom: 2 }}>
              Es existieren ungespeicherte Daten.
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            variant={isClosed ? 'contained' : 'outlined'}
            onClick={() => {
              if (isPendingUpdateLegalTransactionFullObject) return;
              handleClose(checkIfEdited());
            }}
          >
            Schließen
          </Button>
          {!isClosed && (
            <>
              <Button
                variant='contained'
                onClick={() =>
                  legalTransactionObject &&
                  legalTransactionFullObject &&
                  legalTransactionObject.idLegalTransactionType !==
                    legalTransactionFullObject.idLegalTransactionType
                    ? setIsOpenChangeType(true)
                    : handleSave()
                }
                disabled={
                  isPending ||
                  isButtonDisabled() ||
                  (dataCollectionArray.find(x => x.syncWithArnoTop === true) !==
                    undefined &&
                    legalTransactionObject.idArnoTopObject === null)
                }
              >
                {isPending ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <span>Speichern</span>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  'Speichern'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/*/////////////////////////////////////////// NEUER UNTERLAGEN SENDEN ///////////////////////////////////////////  */}

      <Dialog
        open={showSendDocumentsDialogNew}
        onClose={() => setShowSendDocumentsDialogNew(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>Unterlagen senden</DialogTitle>
        <DialogContent>
          <SendDocumentNew
            legalTransactionFullObject={legalTransactionFullObject}
            sendDocument={sendDocumentNew}
            setSendDocument={setSendDocumentNew}
          ></SendDocumentNew>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowSendDocumentsDialogNew(false)}
            color='primary'
            variant='outlined'
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => setShowSendDocumentsDialogConfirmation(true)}
            disabled={
              sendDocumentNew.legalClientArray.length < 1 ||
              sendDocumentNew.documentTypeArray.length < 1
            }
            color='primary'
            variant='contained'
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSendDocumentsDialogConfirmation}
        onClose={() => setShowSendDocumentsDialogConfirmation(false)}
      >
        <DialogTitle>
          Wie sollen die Unterlagen bereitgestellt werden?
        </DialogTitle>
        <DialogContent>
          <FormControl
            fullWidth
            variant='outlined'
            error={false}
            margin='dense'
          >
            <InputLabel id='send-documents-label'>
              Bereitstellungsart
            </InputLabel>
            <Select
              labelId='send-documents-label'
              value={sendDocumentNew.sendOption}
              onChange={event =>
                setSendDocumentNew({
                  ...sendDocumentNew,
                  sendOption: event.target.value as ISendDocumentNewSendOption,
                })
              }
              label='Bereitstellungsart'
            >
              <MenuItem value={SendOption.Documentportal}>
                DiNo-Dokumentenportal
              </MenuItem>
              <MenuItem value={SendOption.Email}>
                E-Mail
                <Typography
                  component='span'
                  sx={{ fontStyle: 'italic', fontSize: '0.8em', ml: 1 }}
                >
                  (Nicht GNotDS-konform!)
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSendDocumentsDialogConfirmation(false);
            }}
            color='primary'
            variant='outlined'
          >
            Abbrechen
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              handleSendDocuments();
              setShowSendDocumentsDialogConfirmation(false);
              setShowSendDocumentsDialogNew(false);
            }}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isOpenDelete} onClose={() => setIsOpenDelete(false)}>
        <DialogTitle>Löschen</DialogTitle>
        <DialogContent>
          Sind Sie sich sicher, dass Sie den aktuellen Vorgang löschen wollen?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpenDelete(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={handleDelete}
            disabled={isPendingDeleteLegalTransaction}
          >
            {isPendingDeleteLegalTransaction ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Löschen</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Löschen'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOpenChangeType}
        onClose={() => setIsOpenChangeType(false)}
      >
        <DialogTitle>Veränderung der Rechtsgeschäftsart</DialogTitle>
        <DialogContent>
          Wollen Sie den aktuellen Vollzugsstand (Abwicklung) und das bestehende
          Rollenschema der Mandanten beibehalten oder auf den Vollzugsstand und
          das Rollenschema der neuen Rechtsgeschäftsart migrieren?
          <br />
          Bei einer Migration geht Ihr aktueller Stand der Abwicklung sowie die
          Rollenverteilung der Mandanten verloren.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpenChangeType(false)}>Abbruch</Button>
          <Button onClick={() => handleSave()}>Beibehalten</Button>
          <Button onClick={() => handleSave(true)}>Migrieren</Button>
        </DialogActions>
      </Dialog>

      {showAddCustomIdDialog && (
        <AddCustomIdDialog
          clients={takenLegalClients.filter(
            c =>
              !customIdsWithContact.some(
                x => x.idLegalClient === c.idLegalClient
              )
          )}
          onAddCustomId={(contactId: number, customId: string) => {
            handleChangeCustomIds(contactId, customId);
            setShowAddCustomIdDialog(false);
          }}
          onClose={() => setShowAddCustomIdDialog(false)}
        />
      )}
    </SidepanelDocumentContext.Provider>
  );
};
